import {
  createElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  ConvexProviderWithAuth,
  ConvexReactClient,
  useConvexAuth,
  useMutation,
  useQuery
} from 'convex/react';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import {
  getAccessToken,
  getSessionKey,
  isCurrentSession,
  renewAccessToken
} from 'utils/authSession';
import { authorizationForSession } from 'utils/authSession';
import { SESSION_KEYS, session } from 'utils/storage';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

import {
  AI_GREETING_START_FAILED_MESSAGE,
  AiAssistantDispatchDeadlineError,
  AiAssistantResponseTimeoutError,
  readErrorClass
} from './chatErrors';

import type {
  AiAssistantConvexTokenExchange,
  AiAssistantImageAttachment,
  AiAssistantLiveChatOffer,
  AiAssistantMessage,
  AiAssistantMessageKind,
  AiAssistantMessageStatus,
  AiAssistantStreamStep,
  AiAssistantThreadDetail,
  AiAssistantThreadSummary,
  AiAssistantTokenRenewalOutcome,
  CreateAiAssistantMessageRequest,
  CreateAiAssistantMessageResult,
  GetAiAssistantThreadResponse,
  GetAiAssistantThreadsResponse
} from './aiAssistantChat.types';

const AI_ASSISTANT_THREAD_KIND = 'patient_portal' as const;
// Contract with convex/patientAuth.ts (PATIENT_ACCESS_TOKEN_HEADER): the patient's own access
// token rides along on /chat-stream so the chat backend can open a patient-scoped call on the
// platform's AI proxy. The backend keeps it in memory for the turn only.
const AI_ASSISTANT_PATIENT_TOKEN_HEADER = 'X-Patient-Access-Token';
const AI_ASSISTANT_WORKFLOW_ID = AI_ASSISTANT_THREAD_KIND;
// The Convex client refetches its token this long before the JWT lapses (default 10 s). The
// exchange goes through the platform's /my-account and, after a portal-token renewal, a second
// round trip; a refetch that starts late can outlive the token it replaces.
const CONVEX_CLIENT_REFRESH_LEEWAY_SECONDS = 60;
// A cached Convex token is handed out only while it outlives the client's own refetch point.
const CONVEX_AUTH_REFRESH_LEEWAY_MS = (CONVEX_CLIENT_REFRESH_LEEWAY_SECONDS + 30) * 1000;
// Waits before retrying a Convex sign-in that failed while the portal session still exists. A
// failed fetch leaves the Convex client signed out and it never retries on its own.
const CONVEX_AUTH_RETRY_DELAYS_MS = [2_000, 5_000, 15_000, 30_000, 60_000];
// Timed retries stop after this many failures in a row (about ten minutes); the network coming
// back, the tab being shown again or another patient signing in still retries.
const CONVEX_AUTH_MAX_TIMED_RETRIES = 12;
const DEFAULT_AI_THREAD_TITLE = 'New Message';

type ConvexThreadSummary = {
  conversationTitle: string;
  createdAt: number;
  id: string;
  latestPreview: string;
  status: 'complete' | 'in_progress';
  updatedAt: number;
};

type ConvexThreadAccess = {
  completedAt: number | null;
  conversationEnded: boolean;
  conversationSeq?: number;
  conversationTitle: string;
  createdAt: number | null;
  endedConversationSeqs?: number[];
  escalationRequestedAt: number | null;
  idleNudgedAt?: number | null;
  isClaimed: boolean;
  liveChatOffer?: AiAssistantLiveChatOffer | null;
  status: 'complete' | 'in_progress' | null;
  threadId: string;
  updatedAt: number | null;
};

type ConvexPromptRecord = {
  assistantKind?: AiAssistantMessageKind | null;
  assistantStatus: 'pending' | 'streaming' | 'done' | 'error' | 'timeout';
  assistantText: string;
  attachments?: ConvexPromptAttachment[] | null;
  conversationSeq?: number;
  createdAt: number;
  id: string;
  idleCloseAt?: number | null;
  isGreeting?: boolean;
  prompt: string;
  streamId: string;
  threadId: string;
};

type ConvexPromptAttachment = {
  attachmentId?: string;
  contentType: string;
  fileName: string;
  fileSize: number;
  storageId: string;
};

type QueryHookState<Data> = {
  data: Data | undefined;
  error: { data: string } | undefined;
  isFetching: boolean;
  isLoading: boolean;
  /** Shown data is being held while Convex signs the patient in again: nothing new can land. */
  isReconnecting?: boolean;
};

type CreateAiAssistantMessageMutation = (
  request: CreateAiAssistantMessageRequest
) => Promise<CreateAiAssistantMessageResult>;

type CachedConvexAuthToken = {
  expiresAt: number;
  patientAccessToken: string;
  token: string;
};

type PatientConvexAuthResponse = {
  expiresAt: number;
  token: string;
};

let convexReactClient: ConvexReactClient | null = null;
let cachedConvexAuthToken: CachedConvexAuthToken | null = null;
let inFlightConvexAuthTokenPromise: Promise<CachedConvexAuthToken | null> | null = null;
let inFlightConvexAuthTokenPatientAccessToken: string | null = null;
const cachedAttachmentBlobs = new Map<string, Blob>();
const inFlightAttachmentBlobRequests = new Map<string, Promise<Blob>>();

const asThreadId = (value: string) => value as Id<'threads'>;
const asThreadUploadId = (value: string) => value as Id<'threadUploads'>;
const asStorageId = (value: string) => value as Id<'_storage'>;

const createResponse = <Data>(data: Data) => ({
  data,
  errorCode: 0 as const,
  info: null,
  message: 'ok'
});

const resolveConvexSiteUrl = () =>
  import.meta.env.VITE_CONVEX_SITE_URL ?? import.meta.env.VITE_CONVEX_URL;

const resolveConvexUrl = () =>
  import.meta.env.VITE_CONVEX_URL ?? import.meta.env.VITE_CONVEX_SITE_URL;

const getRequiredConvexSiteUrl = () => {
  const convexSiteUrl = resolveConvexSiteUrl();

  if (!convexSiteUrl) {
    throw new Error('Missing VITE_CONVEX_SITE_URL or VITE_CONVEX_URL');
  }

  return convexSiteUrl;
};

const getRequiredConvexUrl = () => {
  const convexUrl = resolveConvexUrl();

  if (!convexUrl) {
    throw new Error('Missing VITE_CONVEX_URL or VITE_CONVEX_SITE_URL');
  }

  return convexUrl;
};

const getConvexReactClient = () => {
  if (!convexReactClient) {
    convexReactClient = new ConvexReactClient(getRequiredConvexUrl(), {
      authRefreshTokenLeewaySeconds: CONVEX_CLIENT_REFRESH_LEEWAY_SECONDS
    });
  }

  return convexReactClient;
};

const buildAiAssistantUrl = (path: string) => new URL(path, getRequiredConvexSiteUrl()).toString();
const getAttachmentBlobCacheKey = (attachmentId: string, accessToken: string) =>
  `${accessToken}:${attachmentId}`;

const readErrorMessage = async (response: Response) => {
  const body = await response
    .clone()
    .json()
    .catch(() => null);

  if (body && typeof body === 'object') {
    if ('error' in body && typeof body.error === 'string') {
      return body.error;
    }

    if ('details' in body && typeof body.details === 'string') {
      return body.details;
    }
  }

  const text = await response.text().catch(() => '');
  return text || 'Unable to initialize AI chat.';
};

const useAiAssistantAccessToken = () => {
  const { accessToken } = useAppSelector(selectUser);
  return (accessToken ?? '').trim();
};

type ConvexTokenExchangeResult = {
  body: PatientConvexAuthResponse | null;
  /** The portal access token itself was rejected — renewing the session may fix it; retrying with the same token cannot. */
  unauthorized: boolean;
};

// Who wants to hear about each exchange (the chat panel, for its greeting diagnostics). The
// exchange runs for the Convex client's sign-in and for the stream, at moments no caller controls,
// so a listener is the one place every exchange passes.
const convexTokenExchangeListeners = new Set<(exchange: AiAssistantConvexTokenExchange) => void>();

// Returns how to stop listening.
export const subscribeToConvexTokenExchanges = (
  listener: (exchange: AiAssistantConvexTokenExchange) => void
) => {
  convexTokenExchangeListeners.add(listener);

  return () => {
    convexTokenExchangeListeners.delete(listener);
  };
};

const notifyConvexTokenExchange = (exchange: AiAssistantConvexTokenExchange) =>
  convexTokenExchangeListeners.forEach((listener) => {
    try {
      listener(exchange);
    } catch {
      // Reporting is best effort: a listener that throws must not fail the sign-in.
    }
  });

const exchangePatientTokenForConvexToken = async (
  accessToken: string
): Promise<ConvexTokenExchangeResult> => {
  const startedAt = Date.now();
  const report = (
    outcome: AiAssistantConvexTokenExchange['outcome'],
    httpStatus: number | null,
    errorClass: string | null = null
  ) =>
    notifyConvexTokenExchange({
      durationMs: Date.now() - startedAt,
      errorClass,
      httpStatus,
      outcome
    });
  let response: Response;

  try {
    response = await fetch(buildAiAssistantUrl('/auth/convex-token'), {
      headers: {
        // In the format the session's source takes (`Bearer` for a login-service token, LMD-682).
        Authorization: authorizationForSession(accessToken)
      },
      method: 'POST'
    });
  } catch (error) {
    report('network_error', null, readErrorClass(error));
    throw error;
  }

  if (!response.ok) {
    report(response.status === 401 ? 'unauthorized' : 'refused', response.status);
    return { body: null, unauthorized: response.status === 401 };
  }

  const body = (await response.json().catch(() => null)) as PatientConvexAuthResponse | null;

  if (!body?.token || !Number.isFinite(body.expiresAt)) {
    report('no_token', response.status);
    return { body: null, unauthorized: false };
  }

  report('token', response.status);
  return { body, unauthorized: false };
};

// Staff signed in as the patient ("view as patient") get no chat identity. The chat backend refuses
// the first access token it sees for them (403, staff_session_refused) but cannot tell a renewed
// one apart, because a renewal drops the staff claims (LMD-939), so the portal's marker decides, as
// it does for the AI chat and the Zoom channel. The tab's view-as-patient key is set at staff
// sign-in and kept across token renewals. It is dropped when the portal session is cleared
// (`user/clearUser`, by clearStaffViewAsPatientMiddleware) and when a direct sign-in installs a new
// session (Login, the WorkOS session load and ConfirmAccount; the sign-up completions do not); a
// token renewal is neither. It can change under a mounted provider, so it is read at call time,
// never captured.
const isStaffViewingAsPatient = () => Boolean(session.get(SESSION_KEYS.VIEW_AS_PATIENT));

const getCachedConvexAuthToken = (patientAccessToken: string) => {
  if (
    cachedConvexAuthToken &&
    cachedConvexAuthToken.patientAccessToken === patientAccessToken &&
    cachedConvexAuthToken.expiresAt - CONVEX_AUTH_REFRESH_LEEWAY_MS > Date.now()
  ) {
    return cachedConvexAuthToken;
  }

  return null;
};

const fetchPatientPortalConvexAccessToken = async (
  patientAccessToken: string,
  {
    allowNetworkFetch = true,
    forceRefreshToken = false,
    renewPatientAccessToken
  }: {
    allowNetworkFetch?: boolean;
    forceRefreshToken?: boolean;
    renewPatientAccessToken?: () => Promise<string | null>;
  } = {}
) => {
  if (!patientAccessToken || isStaffViewingAsPatient()) {
    return null;
  }

  if (!forceRefreshToken) {
    const cachedToken = getCachedConvexAuthToken(patientAccessToken);

    if (cachedToken) {
      return cachedToken.token;
    }
  }

  if (
    inFlightConvexAuthTokenPromise &&
    inFlightConvexAuthTokenPatientAccessToken === patientAccessToken
  ) {
    const inFlightToken = await inFlightConvexAuthTokenPromise;
    return inFlightToken?.token ?? null;
  }

  if (!allowNetworkFetch) {
    return null;
  }

  const nextTokenPromise = (async () => {
    try {
      let mintingAccessToken = patientAccessToken;
      let exchange = await exchangePatientTokenForConvexToken(mintingAccessToken);

      // The portal access token lives ten minutes, and this exchange deliberately bypasses the
      // RTK layer that renews it — that layer signs the patient out on failure, far too
      // destructive for a chat identity. So a tab left idle reaches this point with an expired
      // token, and without renewal every owner-scoped query goes null: the thread view shows
      // "Conversation not found" while the page still looks signed in. Renew once, quietly —
      // the same contract as the Zoom credential hook in ZoomChatWidget.
      // The portal's own reauth may have renewed while this exchange was on the wire. The token the
      // store holds now then works, where a second renewal would rotate the pair again.
      if (!exchange.body && exchange.unauthorized) {
        const liveAccessToken = getAccessToken().trim();

        if (liveAccessToken && liveAccessToken !== mintingAccessToken) {
          mintingAccessToken = liveAccessToken;
          exchange = await exchangePatientTokenForConvexToken(mintingAccessToken);
        }
      }

      if (!exchange.body && exchange.unauthorized && renewPatientAccessToken) {
        const renewedAccessToken = await renewPatientAccessToken();

        if (renewedAccessToken) {
          mintingAccessToken = renewedAccessToken;
          exchange = await exchangePatientTokenForConvexToken(mintingAccessToken);
        }
      }

      if (!exchange.body) {
        cachedConvexAuthToken = null;
        return null;
      }

      // Keyed to the token that actually minted it: after a renewal the store holds the renewed
      // access token, so the next lookup has to match this entry rather than miss forever.
      cachedConvexAuthToken = {
        expiresAt: exchange.body.expiresAt,
        patientAccessToken: mintingAccessToken,
        token: exchange.body.token
      };

      return cachedConvexAuthToken;
    } catch {
      cachedConvexAuthToken = null;
      return null;
    }
  })();

  inFlightConvexAuthTokenPromise = nextTokenPromise;
  inFlightConvexAuthTokenPatientAccessToken = patientAccessToken;

  try {
    const nextToken = await nextTokenPromise;
    return nextToken?.token ?? null;
  } finally {
    if (inFlightConvexAuthTokenPromise === nextTokenPromise) {
      inFlightConvexAuthTokenPromise = null;
      inFlightConvexAuthTokenPatientAccessToken = null;
    }
  }
};

/**
 * Whose sign-in the Convex provider started last. For the render in which the store switches
 * patients, `useConvexAuth()` still reports the previous patient signed in (the provider notices
 * only in its effects) and the query cache still holds that patient's answers. The auth hook sets
 * this once the new sign-in has started, so the data hooks never read that render as the new
 * patient's. Null while no provider is mounted: nothing can be cached for anyone then.
 */
let convexSignInIdentityKey: string | null = null;
const convexSignInListeners = new Set<() => void>();

const setConvexSignInIdentityKey = (identityKey: string | null) => {
  convexSignInIdentityKey = identityKey;
  convexSignInListeners.forEach((listener) => listener());
};

const subscribeToConvexSignIn = (listener: () => void) => {
  convexSignInListeners.add(listener);

  return () => {
    convexSignInListeners.delete(listener);
  };
};

const getConvexSignInIdentityKey = () => convexSignInIdentityKey;

const usePatientPortalConvexAuth = () => {
  const { accessToken, userId } = useAppSelector(selectUser) ?? {};
  const trimmedAccessToken = (accessToken ?? '').trim();
  const hasSession = trimmedAccessToken !== '';
  /**
   * Whose Convex identity this is. ConvexProviderWithAuth restarts auth whenever `fetchAccessToken`
   * changes, and it tears the identity down before it sets the new one: the owner-scoped queries
   * rerun signed out, the chat shows "Conversation not found." and rebuilds from the top (captured
   * on the preview, Sept 22). A rotated portal token for the same patient must not do that, so the
   * callback reads the live token instead of closing over it; a different patient must, so the
   * patient id keys it. Until /my-account hydrates the id, the token itself stands in.
   */
  const identityKey = userId || trimmedAccessToken;
  // Bumped to restart Convex auth after a sign-in failed while the session still exists.
  const [retryNonce, setRetryNonce] = useState(0);
  const retryRef = useRef<{
    attempt: number;
    failed: boolean;
    timer?: ReturnType<typeof setTimeout>;
  }>({ attempt: 0, failed: false });

  /**
   * Which mount of this provider a renewal belongs to. Incremented on mount and again on unmount,
   * so a value captured while mounted never matches once the provider has gone. An unmounted
   * provider owns nothing in the store and must not write to it: the previous ref-based guard
   * stopped observing the store on unmount and kept matching the patient who asked, so a renewal
   * finishing after logout re-installed their tokens, and one finishing after sign-in-as-patient
   * put them over the replacement patient's session (F2 in the Sept 8 audit).
   */
  const mountGenerationRef = useRef(0);

  useEffect(() => {
    mountGenerationRef.current += 1;
    const retry = retryRef.current;

    return () => {
      mountGenerationRef.current += 1;
      clearTimeout(retry.timer);
    };
  }, []);

  const restartAuth = useCallback(() => {
    clearTimeout(retryRef.current.timer);
    retryRef.current.timer = undefined;
    setRetryNonce((nonce) => nonce + 1);
  }, []);

  // A failed sign-in retries on a widening timer, and at once when the network comes back or the
  // tab is shown again: a laptop waking from sleep reaches the exchange before its network does.
  const scheduleRetry = useCallback(() => {
    const retry = retryRef.current;

    if (retry.timer || retry.attempt >= CONVEX_AUTH_MAX_TIMED_RETRIES) {
      return;
    }

    const delay =
      CONVEX_AUTH_RETRY_DELAYS_MS[Math.min(retry.attempt, CONVEX_AUTH_RETRY_DELAYS_MS.length - 1)];
    retry.attempt += 1;
    retry.timer = setTimeout(() => {
      retry.timer = undefined;

      // The client's own forced refetch may have signed in since: restarting then would tear
      // down a good identity.
      if (retry.failed) {
        restartAuth();
      }
    }, delay);
  }, [restartAuth]);

  // Another patient starts from a clean slate.
  useEffect(() => {
    const retry = retryRef.current;

    return () => {
      clearTimeout(retry.timer);
      retry.timer = undefined;
      retry.attempt = 0;
      retry.failed = false;
    };
  }, [identityKey]);

  // The provider's own effects run first (they belong to its children) and have started the sign-in
  // for this patient by now, so from here a settled flag can only be this patient's.
  useEffect(() => {
    setConvexSignInIdentityKey(identityKey);

    return () => setConvexSignInIdentityKey(null);
  }, [identityKey]);

  useEffect(() => {
    const retryIfFailed = () => {
      if (retryRef.current.failed && document.visibilityState === 'visible') {
        restartAuth();
      }
    };

    window.addEventListener('online', retryIfFailed);
    document.addEventListener('visibilitychange', retryIfFailed);

    return () => {
      window.removeEventListener('online', retryIfFailed);
      document.removeEventListener('visibilitychange', retryIfFailed);
    };
  }, [restartAuth]);

  // The shared renewal checks the live store before installing: the session can end or be REPLACED
  // while the renewal is in flight — logout clears it, sign-in-as-patient and deep-link flows swap
  // one patient for another in the same document — and installing then would resurrect a signed-out
  // session or hand this patient's tokens to the next one. The mount generation is this provider's
  // own veto, checked before anything is installed.
  const renewPatientAccessToken = useCallback(() => {
    const mountGeneration = mountGenerationRef.current;

    return renewAccessToken(getSessionKey(), () => mountGenerationRef.current === mountGeneration);
  }, []);

  // The callback the provider is signed in with now. A fetch that belongs to an earlier one (Convex
  // discards its answer as outdated) must not touch the retry state of the current one.
  const currentFetchRef = useRef<unknown>(null);

  const fetchAccessToken = useCallback(
    async function fetchToken({ forceRefreshToken }: { forceRefreshToken: boolean }) {
      // `identityKey` and `retryNonce` are restart triggers: a new callback makes the provider
      // sign in again. The token itself is read from the store at call time.
      void identityKey;
      void retryNonce;

      // Staff get no token and no retry: the portal withholds the identity for the whole session,
      // so there is no failure to wait out, and a restart would only tear the Convex identity down
      // again for nothing.
      if (isStaffViewingAsPatient()) {
        return null;
      }

      const liveAccessToken = getAccessToken().trim();
      // Unforced, only a cached token is handed out: a miss answers null and Convex fetches again
      // with forceRefreshToken while the socket stays paused, so nothing runs signed out.
      const token = await fetchPatientPortalConvexAccessToken(liveAccessToken, {
        allowNetworkFetch: forceRefreshToken,
        forceRefreshToken,
        renewPatientAccessToken
      });
      const retry = retryRef.current;

      if (currentFetchRef.current !== fetchToken) {
        return token;
      }

      if (token) {
        clearTimeout(retry.timer);
        retry.timer = undefined;
        retry.attempt = 0;
        retry.failed = false;
      } else if (liveAccessToken && forceRefreshToken) {
        retry.failed = true;
        scheduleRetry();
      }

      return token;
    },
    [identityKey, renewPatientAccessToken, retryNonce, scheduleRetry]
  );

  useEffect(() => {
    currentFetchRef.current = fetchAccessToken;
  }, [fetchAccessToken]);

  return useMemo(
    () => ({
      fetchAccessToken,
      isAuthenticated: hasSession,
      isLoading: false
    }),
    [fetchAccessToken, hasSession]
  );
};

const mapAssistantStatus = (
  status: ConvexPromptRecord['assistantStatus']
): AiAssistantMessageStatus => (status === 'timeout' ? 'error' : status);

const mapThreadStatus = (
  status: ConvexThreadSummary['status'] | ConvexThreadAccess['status']
): AiAssistantMessageStatus | null => {
  if (status === 'in_progress') {
    return 'pending';
  }

  return null;
};

const mapPromptAttachment = (attachment: ConvexPromptAttachment): AiAssistantImageAttachment => ({
  attachmentId: attachment.attachmentId ?? attachment.storageId,
  contentType: attachment.contentType,
  fileName: attachment.fileName,
  fileSize: attachment.fileSize,
  storageId: attachment.storageId
});

const mapThreadSummary = (thread: ConvexThreadSummary): AiAssistantThreadSummary => ({
  conversationTitle: thread.conversationTitle || DEFAULT_AI_THREAD_TITLE,
  createdAt: thread.createdAt,
  id: thread.id,
  latestAssistantStatus: mapThreadStatus(thread.status),
  latestPreview: thread.latestPreview,
  updatedAt: thread.updatedAt,
  workflowId: AI_ASSISTANT_WORKFLOW_ID
});

const mapPromptToMessages = (prompt: ConvexPromptRecord): AiAssistantMessage[] => {
  const isGreeting = prompt.isGreeting ?? false;
  // A greeting turn is agent-only ONLY when there's no patient text to show alongside it.
  // When the patient supplied intake detail on the reason picker, that text is echoed onto
  // this same row's `prompt` field so it renders as their first bubble, confirming to the
  // patient that what they typed was actually received.
  const hasUserPrompt = Boolean(prompt.prompt?.trim());
  const userMessageId = `user:${prompt.id}`;
  const assistantStatus = mapAssistantStatus(prompt.assistantStatus);
  // A greeting row the backend marked error before it wrote any text means the chat never started
  // for the patient, whether the watchdog gave up on it (never delivered, or claimed and then
  // died) or the provider failed on it. The watchdog marks a greeting it never received as error
  // two minutes after the start; without this the row swapped the browser's own failure sentence
  // for a different one, so one failure read as two. A greeting that streamed some text and then
  // died keeps the general line, because the patient saw part of an answer.
  const isUndeliveredGreeting = isGreeting && !prompt.assistantText?.trim();
  const failureMessage = isUndeliveredGreeting
    ? AI_GREETING_START_FAILED_MESSAGE
    : 'Unable to complete this response.';

  const conversationSeq = prompt.conversationSeq ?? 1;
  const assistantMessage: AiAssistantMessage = {
    body: prompt.assistantText,
    conversationSeq,
    createdAt: prompt.createdAt,
    errorMessage: assistantStatus === 'error' ? failureMessage : null,
    id: `assistant:${prompt.streamId}`,
    idleCloseAt: prompt.idleCloseAt ?? null,
    isGreeting,
    kind: prompt.assistantKind ?? null,
    parentMessageId: hasUserPrompt ? userMessageId : null,
    role: 'assistant',
    status: assistantStatus,
    streamId: prompt.streamId,
    updatedAt: prompt.createdAt,
    workflowId: AI_ASSISTANT_WORKFLOW_ID
  };

  if (!hasUserPrompt) {
    return [assistantMessage];
  }

  return [
    {
      attachments: prompt.attachments?.map(mapPromptAttachment) ?? [],
      body: prompt.prompt,
      conversationSeq,
      createdAt: prompt.createdAt,
      errorMessage: null,
      id: userMessageId,
      parentMessageId: null,
      role: 'user',
      status: 'done',
      streamId: prompt.streamId,
      updatedAt: prompt.createdAt,
      workflowId: AI_ASSISTANT_WORKFLOW_ID
    },
    assistantMessage
  ];
};

const mapThreadDetail = (
  access: ConvexThreadAccess,
  prompts: ConvexPromptRecord[]
): AiAssistantThreadDetail => ({
  conversationEnded: access.conversationEnded,
  conversationSeq: access.conversationSeq ?? 1,
  conversationTitle: access.conversationTitle || DEFAULT_AI_THREAD_TITLE,
  createdAt: access.createdAt ?? 0,
  endedConversationSeqs: access.endedConversationSeqs ?? (access.conversationEnded ? [1] : []),
  escalationRequestedAt: access.escalationRequestedAt ?? null,
  id: access.threadId,
  idleNudgedAt: access.idleNudgedAt ?? null,
  latestAssistantStatus: mapThreadStatus(access.status),
  latestPreview: '',
  liveChatOffer: access.liveChatOffer ?? null,
  messages: prompts.flatMap(mapPromptToMessages),
  updatedAt: access.updatedAt ?? access.createdAt ?? 0,
  workflowId: AI_ASSISTANT_WORKFLOW_ID
});

// The portal's access token lives ten minutes. A patient who reads the assistant's answer for a
// while and then types again would otherwise hand the chat backend an expired token: the AI proxy
// validates it on every turn (convex/aiProxy.ts) and the whole turn fails. Renew it first when it
// is about to lapse, under the same session guard the Convex auth exchange uses, so a renewal that
// lands after logout or after sign-in-as-patient never installs the wrong patient's tokens.
const PATIENT_TOKEN_RENEWAL_LEEWAY_MS = 60_000;

const readJwtExpiryMs = (token: string): number | null => {
  const payload = token.split('.')[1];

  if (!payload) {
    return null;
  }

  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(
      atob(base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '='))
    ) as {
      exp?: unknown;
    };

    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
};

// `onRenewal` is told what a renewal that was needed came to, and is not called when none was.
export const ensureFreshPatientAccessToken = async (
  accessToken: string,
  onRenewal?: (outcome: AiAssistantTokenRenewalOutcome) => void
): Promise<string> => {
  const expiresAt = readJwtExpiryMs(accessToken);

  // Not a JWT we can read, or still good for a while: send it as is.
  if (expiresAt === null || expiresAt - Date.now() > PATIENT_TOKEN_RENEWAL_LEEWAY_MS) {
    return accessToken;
  }

  const sessionKey = getSessionKey();
  const renewed = await renewAccessToken(sessionKey);

  if (renewed) {
    onRenewal?.('renewed');
    return renewed;
  }

  // The shared renewal answers null for every refusal; a session that changed hands meanwhile is
  // told apart, and the rest (no refresh token, a failed renewal) reports as null.
  onRenewal?.(isCurrentSession(sessionKey) ? 'null' : 'session_changed');

  return accessToken;
};

// Throws AiAssistantDispatchDeadlineError when `deadlineAt` has passed. Checked before each token
// request as well as before fetch, so a try already out of time does not start a renewal (which can
// rotate the patient's tokens) or a Convex token exchange it can no longer use.
const refuseAfterDispatchDeadline = (deadlineAt: number | undefined) => {
  if (deadlineAt !== undefined && Date.now() >= deadlineAt) {
    throw new AiAssistantDispatchDeadlineError();
  }
};

// Waits for `work`, or rejects with AiAssistantDispatchDeadlineError once `deadlineAt` passes,
// whichever comes first. Without a deadline it is a plain wait. The work is not cancelled: a token
// request still finishes in the background, and its result is ignored here.
const waitBeforeDispatchDeadline = async <T>(
  work: Promise<T>,
  deadlineAt: number | undefined
): Promise<T> => {
  if (deadlineAt === undefined) {
    return await work;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    return await Promise.race([
      work,
      new Promise<never>((_resolve, reject) => {
        timer = setTimeout(
          () => reject(new AiAssistantDispatchDeadlineError()),
          Math.max(0, deadlineAt - Date.now())
        );
      })
    ]);
  } finally {
    clearTimeout(timer);
  }
};

// `onStep` reports how far the POST got (see AiAssistantStreamStep). The line between a failure the
// caller may retry and one it may not is the response: any failure before a `post_result` with a
// status (no token, or a `fetch` that rejected) got no answer from the server, and the server
// claims a turn before it streams it (claimChatTurn), so a second POST for the same stream is
// safe. From a status on, the server has the turn.
//
// `dispatchDeadlineAt` (epoch ms) is the last moment the POST may go out. Without one the stream
// is sent however long the tokens take. With one, the token waits are cut off at it and the time is
// checked again right before fetch, so no POST leaves after it: the call rejects with
// AiAssistantDispatchDeadlineError and reports no `post_dispatched`.
//
// `responseTimeoutMs` is how long the POST may wait for its response headers once it has gone out.
// When it passes the request is aborted and the call rejects with AiAssistantResponseTimeoutError,
// after reporting a `post_result` with that error class and a null status. Without one the request
// waits however long the network takes. The timer stops when the headers arrive: the body may then
// stream as long as it needs.
export const streamAiAssistantMessage = async ({
  accessToken,
  dispatchDeadlineAt,
  onChunk,
  onStep,
  responseTimeoutMs,
  streamId
}: {
  accessToken: string;
  dispatchDeadlineAt?: number;
  onChunk: (chunk: string) => void;
  onStep?: (step: AiAssistantStreamStep) => void;
  responseTimeoutMs?: number;
  streamId: string;
}) => {
  // Reporting is best effort: a listener that throws must not turn a good request into a failed one.
  const notify = (step: AiAssistantStreamStep) => {
    try {
      onStep?.(step);
    } catch {
      // Nothing to do: the stream carries on without the report.
    }
  };

  refuseAfterDispatchDeadline(dispatchDeadlineAt);

  const patientAccessToken = await waitBeforeDispatchDeadline(
    ensureFreshPatientAccessToken(accessToken, (outcome) =>
      notify({ outcome, step: 'patient_token_renewal' })
    ),
    dispatchDeadlineAt
  );

  refuseAfterDispatchDeadline(dispatchDeadlineAt);

  const convexAccessToken = await waitBeforeDispatchDeadline(
    fetchPatientPortalConvexAccessToken(patientAccessToken),
    dispatchDeadlineAt
  );

  if (!convexAccessToken) {
    notify({ step: 'convex_token_missing' });
    throw new Error('Unable to authenticate AI chat stream.');
  }

  // A timer can fire late (a background tab, a busy main thread), so the waits above alone do not
  // promise the POST leaves in time.
  refuseAfterDispatchDeadline(dispatchDeadlineAt);

  notify({ step: 'post_dispatched' });

  const abortController = responseTimeoutMs === undefined ? undefined : new AbortController();
  const responseTimer =
    abortController === undefined
      ? undefined
      : setTimeout(
          () => abortController.abort(new AiAssistantResponseTimeoutError()),
          responseTimeoutMs
        );
  let response: Response;

  try {
    response = await fetch(buildAiAssistantUrl('/chat-stream'), {
      body: JSON.stringify({
        kind: AI_ASSISTANT_THREAD_KIND,
        streamId
      }),
      headers: {
        [AI_ASSISTANT_PATIENT_TOKEN_HEADER]: authorizationForSession(patientAccessToken),
        Authorization: `Bearer ${convexAccessToken}`,
        'Content-Type': 'application/json'
      },
      method: 'POST',
      // A browser that ignores an abort reason rejects with a DOMException named AbortError instead
      // of the timeout error. Either way fetch rejected before any status, which is all the caller
      // reads from it.
      ...(abortController && { signal: abortController.signal })
    });
  } catch (error) {
    notify({ errorClass: readErrorClass(error), status: null, step: 'post_result' });
    throw error;
  } finally {
    // Headers arrived or the request failed: either way the wait the timer bounds is over.
    clearTimeout(responseTimer);
  }

  notify({ errorClass: null, status: response.status, step: 'post_result' });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  if (!response.body) {
    // 204 and 205 are null-body statuses, so a browser may hand back no stream at all for them. The
    // server answers 205 when it has nothing to send because the stream already started (a retry
    // that took over the claim after the first request finished). That is an answer, like the
    // empty 200 a claimed turn gets: the text arrives through the live message query.
    if (response.status === 204 || response.status === 205) {
      return;
    }

    throw new Error('AI chat stream returned no body.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  for (;;) {
    const { done, value } = await reader.read();
    if (done) {
      if (value) {
        onChunk(decoder.decode(value));
      }

      return;
    }

    onChunk(decoder.decode(value, { stream: true }));
  }
};

export const fetchAiAssistantAttachmentBlob = async (attachmentId: string, accessToken: string) => {
  const cacheKey = getAttachmentBlobCacheKey(attachmentId, accessToken);
  const cachedBlob = cachedAttachmentBlobs.get(cacheKey);

  if (cachedBlob) {
    return cachedBlob;
  }

  const inFlightRequest = inFlightAttachmentBlobRequests.get(cacheKey);

  if (inFlightRequest) {
    return await inFlightRequest;
  }

  const nextRequest = (async () => {
    const convexAccessToken = await fetchPatientPortalConvexAccessToken(accessToken);
    const response = await fetch(
      `${buildAiAssistantUrl('/thread-uploads/image')}?attachmentId=${encodeURIComponent(attachmentId)}`,
      {
        headers: {
          Authorization: convexAccessToken
            ? `Bearer ${convexAccessToken}`
            : authorizationForSession(accessToken)
        },
        method: 'GET'
      }
    );

    if (!response.ok) {
      throw new Error(await readErrorMessage(response));
    }

    const blob = await response.blob();
    cachedAttachmentBlobs.set(cacheKey, blob);
    return blob;
  })();

  inFlightAttachmentBlobRequests.set(cacheKey, nextRequest);

  try {
    return await nextRequest;
  } finally {
    if (inFlightAttachmentBlobRequests.get(cacheKey) === nextRequest) {
      inFlightAttachmentBlobRequests.delete(cacheKey);
    }
  }
};

export const fetchAiAssistantAttachmentBlobUrl = async (
  attachmentId: string,
  accessToken: string
) => {
  const blob = await fetchAiAssistantAttachmentBlob(attachmentId, accessToken);
  return URL.createObjectURL(blob);
};

// Logout closes the patient's AI proxy sessions while the Convex sign-in still holds (LMD-1820).
// Best effort and bounded, so the sign-out never waits on it longer than this; the records expire
// on the proxy anyway.
const PROXY_RELEASE_ON_LOGOUT_TIMEOUT_MS = 1_500;

export const releaseAiAssistantProxySessions = async () => {
  const client = convexReactClient;

  if (!client) {
    return;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;

  try {
    await Promise.race([
      client.mutation(api.conversationEnd.releaseMyAiProxyCalls, {}),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, PROXY_RELEASE_ON_LOGOUT_TIMEOUT_MS);
      })
    ]);
  } catch {
    // Already signed out or offline: the idle close releases the records later.
  } finally {
    clearTimeout(timer);
  }
};

export const PatientPortalConvexProvider = ({ children }: { children: ReactNode }) =>
  createElement(
    ConvexProviderWithAuth,
    {
      client: getConvexReactClient(),
      useAuth: usePatientPortalConvexAuth
    },
    children
  );

/**
 * Whether Convex has confirmed THIS patient's identity. While it has not (a sign-in in flight, or
 * one that failed and is retrying), the owner-scoped queries answer as if the patient had no
 * threads: an empty list, a null thread. That is not a missing conversation, so the hooks keep
 * what they last showed instead of reporting it gone. Right after a patient switch the flag still
 * describes the previous patient; the provider's sign-in key says whose it is.
 */
const useConvexIdentitySettled = (identityKey: string) => {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const signInIdentityKey = useSyncExternalStore(
    subscribeToConvexSignIn,
    getConvexSignInIdentityKey
  );

  return (
    isAuthenticated &&
    !isLoading &&
    (signInIdentityKey === null || signInIdentityKey === identityKey)
  );
};

// Whose data a hook may hold across a sign-in: never another patient's. The same key as the auth
// hook's, so a patient switch in the same document starts from nothing.
const useAiAssistantIdentity = () => {
  const { accessToken, userId } = useAppSelector(selectUser) ?? {};
  const trimmedAccessToken = (accessToken ?? '').trim();

  return { accessToken: trimmedAccessToken, identityKey: userId || trimmedAccessToken };
};

/**
 * The Convex sign-in as the chat sees it: the client's own flags, and whether the identity gate the
 * data hooks apply is open for this patient. For diagnostics: while the gate is closed a thread
 * shows no data, so a pending greeting stays invisible and is never sent.
 */
export const useAiAssistantConvexAuthState = () => {
  const { isAuthenticated, isLoading } = useConvexAuth();
  const identity = useAiAssistantIdentity();
  const isIdentitySettled = useConvexIdentitySettled(identity.identityKey);

  return { isAuthenticated, isIdentitySettled, isLoading };
};

// Held data belongs to this patient when it was shown under their key, or under the token that
// stood in for it until /my-account hydrated the id: the same session, so the sign-in restart that
// follows the hydration must not blank the view.
const isShownToThisPatient = (
  shownFor: string,
  identity: ReturnType<typeof useAiAssistantIdentity>
) =>
  shownFor === identity.identityKey ||
  (identity.accessToken !== '' && shownFor === identity.accessToken);

export const useGetAiAssistantThreadsQuery = (
  arg?: void | typeof skipToken
): QueryHookState<GetAiAssistantThreadsResponse> => {
  const accessToken = useAiAssistantAccessToken();
  const identity = useAiAssistantIdentity();
  const identitySettled = useConvexIdentitySettled(identity.identityKey);
  const shouldSkip = arg === skipToken || !accessToken;
  const threads = useQuery(api.threadAccess.listThreads, shouldSkip ? 'skip' : {}) as
    ConvexThreadSummary[] | undefined;
  const fresh = useMemo(
    () => (threads ? createResponse(threads.map(mapThreadSummary)) : undefined),
    [threads]
  );
  const [lastShown, setLastShown] = useState<{
    data: GetAiAssistantThreadsResponse;
    identityKey: string;
  } | null>(null);

  useEffect(() => {
    if (fresh && identitySettled) {
      setLastShown({ data: fresh, identityKey: identity.identityKey });
    }
  }, [fresh, identitySettled, identity.identityKey]);

  const held =
    !identitySettled && lastShown && isShownToThisPatient(lastShown.identityKey, identity)
      ? lastShown.data
      : undefined;
  // Until this patient's sign-in is confirmed the query's answer is not theirs to show: signed out
  // it is an empty list (the sidebar must not settle on a list without the AI thread), and right
  // after a patient switch it is still the previous patient's list.
  const data = shouldSkip ? undefined : identitySettled ? fresh : held;
  const isLoading = !shouldSkip && data === undefined;

  return {
    data,
    error: undefined,
    isFetching: isLoading,
    isLoading
  };
};

export const useGetAiAssistantThreadQuery = (
  threadId: string | typeof skipToken
): QueryHookState<GetAiAssistantThreadResponse> => {
  const accessToken = useAiAssistantAccessToken();
  const shouldSkip = threadId === skipToken || !accessToken;
  const threadArgs = shouldSkip
    ? 'skip'
    : {
        threadId: asThreadId(threadId as string)
      };
  const access = useQuery(api.threadAccess.getThreadAccess, threadArgs) as
    ConvexThreadAccess | null | undefined;
  const prompts = useQuery(api.threadMessages.listChatMessages, threadArgs) as
    ConvexPromptRecord[] | undefined;
  const identity = useAiAssistantIdentity();
  const identitySettled = useConvexIdentitySettled(identity.identityKey);
  const fresh = useMemo(
    () =>
      access && access.createdAt !== null && prompts
        ? createResponse(mapThreadDetail(access, prompts))
        : undefined,
    [access, prompts]
  );
  const [lastShown, setLastShown] = useState<{
    data: GetAiAssistantThreadResponse;
    identityKey: string;
    threadId: string;
  } | null>(null);
  const shownThreadId = shouldSkip ? null : (threadId as string);

  useEffect(() => {
    if (fresh && identitySettled && shownThreadId) {
      setLastShown({ data: fresh, identityKey: identity.identityKey, threadId: shownThreadId });
    }
  }, [fresh, identitySettled, identity.identityKey, shownThreadId]);

  const held =
    !identitySettled &&
    lastShown !== null &&
    lastShown.threadId === shownThreadId &&
    isShownToThisPatient(lastShown.identityKey, identity)
      ? lastShown.data
      : undefined;
  // Unconfirmed, the cache can still hold the previous patient's conversation: only held data shows.
  const data = shouldSkip ? undefined : identitySettled ? fresh : held;
  const isLoading =
    !shouldSkip && !data && (access === undefined || prompts === undefined || !identitySettled);
  // Only a signed-in answer can say the thread does not exist.
  const isMissing =
    !shouldSkip && identitySettled && (access === null || access?.createdAt === null);

  return {
    data,
    error: isMissing ? { data: 'Conversation not found.' } : undefined,
    isFetching: isLoading,
    isLoading,
    isReconnecting: !shouldSkip && !identitySettled && !!data
  };
};

export const useThreadWorkflowProvider = (
  threadId: string | null
): 'stack' | 'bland' | null | undefined => {
  const provider = useQuery(
    api.threadMessages.getThreadWorkflowProvider,
    threadId ? { threadId: threadId as Id<'threads'> } : 'skip'
  ) as 'stack' | 'bland' | null | undefined;

  return provider;
};

export const useCreateAiAssistantMessageMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const createChatPrompt = useMutation(api.threadMessages.createChatPrompt);
  const [isLoading, setIsLoading] = useState(false);

  const mutate: CreateAiAssistantMessageMutation = async ({ attachmentIds, prompt, threadId }) => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      const response = await createChatPrompt({
        attachmentIds: attachmentIds?.map(asThreadUploadId),
        prompt,
        threadId: threadId ? asThreadId(threadId) : undefined,
        workflowKind: AI_ASSISTANT_THREAD_KIND
      });

      if (response.workflowKind !== AI_ASSISTANT_THREAD_KIND) {
        throw new Error('AI chat workflow kind mismatch.');
      }

      return createResponse({
        streamId: response.streamId,
        threadId: response.threadId,
        workflowKind: AI_ASSISTANT_THREAD_KIND
      });
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

export const useGenerateAiAssistantImageUploadUrlMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const generateUploadUrl = useMutation(api.threadMessages.generateThreadImageUploadUrl);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async () => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      return await generateUploadUrl({});
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

export const useRegisterAiAssistantImageUploadMutation = () => {
  const accessToken = useAiAssistantAccessToken();
  const registerUpload = useMutation(api.threadMessages.registerThreadImageUpload);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async ({ fileName, storageId }: { fileName: string; storageId: string }) => {
    if (!accessToken) {
      throw new Error('Missing AI assistant access token.');
    }

    setIsLoading(true);

    try {
      return await registerUpload({ fileName, storageId: asStorageId(storageId) });
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};

/**
 * The chat intake vocabulary. A superset of the Click-to-Call topics: 'orders' exists only here,
 * because order status is the lane the AI assistant actually answers (the WISMO pathway) — the
 * other reasons route to regular messaging at the picker level and never start an AI thread from
 * the intake screen.
 */
export type AiChatReason = 'account' | 'medication' | 'orders' | 'other';

// The one reason the AI assistant actually answers (the order-status pathway). The intake
// screen routes every other reason to regular messaging, so both the picker's CTA label and
// the Messages submit handler key off this value.
export const AI_CHAT_REASON: AiChatReason = 'orders';

// Query parameter the Home card adds to `new-ai-chat=true` so the Messages page starts the
// order chat at once instead of showing the intake screen (Dominique, Sept 9 walk-through).
// `Create new request` in Messages still opens the intake screen, where the other reasons
// route to a regular support request.
export const AI_CHAT_REASON_PARAM = 'ai-chat-reason';

type StartAiChatRequest = {
  firstName?: string;
  idempotencyKey: string;
  reason: AiChatReason;
  reasonDetail?: string;
  sourceContext?: { route: string };
  /** Start the next conversation on this thread (the ended-conversation footer) instead of looking the WISMO thread up. */
  threadId?: string;
};

type StartAiChatResult = {
  // The backend keeps one conversation per patient. `resumed` means their thread was still
  // open, so they land on it instead of a new one and no greeting is streamed. Optional until
  // every deployment returns it; a missing flag reads as a fresh start.
  resumed?: boolean;
  streamId: string;
  threadId: string;
  workflowKind: string;
};

/** The patient's Yes / No under the idle nudge: close the conversation now, or keep it open. */
export const useRespondToIdleNudgeMutation = () => {
  const respond = useMutation(api.conversationEnd.respondToIdleNudge);

  return async (request: { close: boolean; threadId: string }) =>
    await respond({ close: request.close, threadId: asThreadId(request.threadId) });
};

export const useStartAiChatMutation = () => {
  const startAiChat = useMutation(api.threadMessages.startAiChat);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = async (request: StartAiChatRequest): Promise<StartAiChatResult> => {
    setIsLoading(true);

    try {
      const { threadId, ...rest } = request;

      return await startAiChat(threadId ? { ...rest, threadId: asThreadId(threadId) } : rest);
    } finally {
      setIsLoading(false);
    }
  };

  return [mutate, { isLoading }] as const;
};
